import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "@oai/platform/ui/Button";
import { FileSelector } from "@oai/platform/ui/FileSelector";
import { FileUpload, Link as LinkIcon, Pencil, Reload, Warning, X } from "@oai/platform/ui/Icon";
import { Input } from "@oai/platform/ui/Input";
import { Modal } from "@oai/platform/ui/Modal";
import { RadioGroup } from "@oai/platform/ui/RadioGroup";
import { Select } from "@oai/platform/ui/Select";

import {
  getClickObjectiveMaxBidValidationError,
  getDefaultClickMaxBidAmountForCurrency,
  getImpressionObjectiveMaxBidValidationError,
} from "@/components/creation-wizard/creationWizardBidding";
import { ONBOARDING_BUSINESS_NAME_MAX_LENGTH } from "@/components/onboarding/OnboardingFormTypes";
import { getOnboardingBusinessNameError } from "@/components/onboarding/onboardingValidation";
import {
  buildCarpetLiteDestinationUrl,
  CARPET_LITE_AD_BODY_MAX_LENGTH,
  CARPET_LITE_AD_TITLE_MAX_LENGTH,
  CARPET_LITE_TARGET_URL_MAX_LENGTH,
} from "@/lib/carpetLiteCampaign";
import { normalizePublicWebsiteUrl } from "@/lib/websiteUrl";
import { messages } from "@/localization/CarpetLite.messages";
import { validateAdDescription, validateAdTitle } from "@/routes/adManager/AdsManagerValidation";
import { useAdGroupMaxBidCapMicrosByCurrency } from "@/routes/adManager/BidStrength";
import {
  getCampaignBudgetMinimumAmount,
  getCampaignBudgetMinimumErrorMessage,
  useDailyCampaignBudgetConfig,
} from "@/routes/adManager/CampaignBudgetUtils";

import type { CarpetLiteCampaignSettings } from "@/lib/carpetLiteCampaign";

export type CarpetLiteEditableCreative = {
  body: string;
  title: string;
};

export type CarpetLiteUploadedLogo = {
  fileId: string;
  previewUrl: string;
};

type CountryOption = { label: string; value: string };

const DEFAULT_ACCOUNT_CURRENCY = "USD";

function clampDestinationUrlSelection(input: HTMLInputElement, immutablePrefixLength: number) {
  const selectionStart = Math.max(
    input.selectionStart ?? immutablePrefixLength,
    immutablePrefixLength,
  );
  const selectionEnd = Math.max(input.selectionEnd ?? immutablePrefixLength, immutablePrefixLength);
  if (selectionStart !== input.selectionStart || selectionEnd !== input.selectionEnd) {
    input.setSelectionRange(selectionStart, selectionEnd);
  }
}

function getDestinationUrlInputPrefix(destinationUrl: string): string {
  return destinationUrl.replace(/\/(?=[?#]|$)/, "");
}

function DialogHeader({
  title,
  description,
  closeLabel,
}: {
  title: string;
  description: string;
  closeLabel: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <Modal.Title className="m-0 truncate text-[20px] leading-[25px] tracking-[-0.46px]">
          {title}
        </Modal.Title>
        <Modal.Description visuallyHidden>{description}</Modal.Description>
      </div>
      <Modal.Close>
        <Button
          aria-label={closeLabel}
          color="secondary"
          iconSize="lg"
          size="lg"
          type="button"
          uniform
          variant="ghost"
        >
          <X aria-hidden="true" />
        </Button>
      </Modal.Close>
    </div>
  );
}

function CharacterCount({ length, maxLength }: { length: number; maxLength: number }) {
  return (
    <span aria-hidden="true" className="text-tertiary text-xs tabular-nums">
      {length}/{maxLength}
    </span>
  );
}

export function CarpetLiteEditAdDialog({
  advertiserName,
  creative,
  generatedImageUrl,
  isRegeneratingImage,
  logoUrl,
  open,
  onClose,
  onRegenerateImage,
  onSave,
  onUploadLogo,
  websiteUrl,
}: {
  advertiserName: string;
  creative: CarpetLiteEditableCreative;
  generatedImageUrl: string | null;
  isRegeneratingImage: boolean;
  logoUrl: string | null;
  open: boolean;
  onClose: () => void;
  onRegenerateImage: () => Promise<boolean>;
  onSave: (
    creative: CarpetLiteEditableCreative,
    advertiserName: string,
    logoOverride: CarpetLiteUploadedLogo | null | undefined,
  ) => void;
  onUploadLogo: (source: File | string) => Promise<CarpetLiteUploadedLogo>;
  websiteUrl: string;
}) {
  const intl = useIntl();
  const [draft, setDraft] = useState(creative);
  const [draftAdvertiserName, setDraftAdvertiserName] = useState(advertiserName);
  const [draftLogoOverride, setDraftLogoOverride] = useState<CarpetLiteUploadedLogo | null>();
  const [isEditingBusinessDetails, setIsEditingBusinessDetails] = useState(false);
  const [logoSource, setLogoSource] = useState<"url" | "upload">("url");
  const [draftLogoUrl, setDraftLogoUrl] = useState(websiteUrl);
  const [didLogoImageFail, setDidLogoImageFail] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoUploadFailed, setLogoUploadFailed] = useState(false);
  const [isImageRemoved, setIsImageRemoved] = useState(false);
  const [regenerationFailed, setRegenerationFailed] = useState(false);
  const wasOpenRef = useRef(false);
  const logoUploadVersionRef = useRef(0);
  const advertiserFeedbackId = useId();
  const logoFeedbackId = useId();
  const titleFeedbackId = useId();
  const bodyFeedbackId = useId();
  const imageStatusId = useId();

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setDraft(creative);
      setDraftAdvertiserName(advertiserName);
      setDraftLogoOverride(undefined);
      setIsEditingBusinessDetails(false);
      setLogoSource("url");
      setDraftLogoUrl(websiteUrl);
      setDidLogoImageFail(false);
      setIsUploadingLogo(false);
      setLogoUploadFailed(false);
      setIsImageRemoved(false);
      setRegenerationFailed(false);
    }
    if (!open && wasOpenRef.current) {
      logoUploadVersionRef.current += 1;
    }
    wasOpenRef.current = open;
  }, [advertiserName, creative, open, websiteUrl]);

  const advertiserNameError = getOnboardingBusinessNameError(draftAdvertiserName, intl);
  const titleError = validateAdTitle(draft.title);
  const bodyError =
    validateAdDescription(draft.body, { requireMinimumLength: true }) ??
    (draft.body.length > CARPET_LITE_AD_BODY_MAX_LENGTH ?
      intl.formatMessage(messages.adDescriptionTooLong, {
        maxLength: CARPET_LITE_AD_BODY_MAX_LENGTH,
      })
    : undefined);
  const hasImage = !isImageRemoved && Boolean(generatedImageUrl?.trim());
  const displayedLogoUrl =
    draftLogoOverride === undefined ? logoUrl : draftLogoOverride?.previewUrl ?? null;
  const hasLogo = displayedLogoUrl != null && !didLogoImageFail;
  let normalizedLogoUrl: string | null = null;
  try {
    normalizedLogoUrl = normalizePublicWebsiteUrl(draftLogoUrl);
  } catch {
    // A safe localized validation message is rendered next to the URL input.
  }
  const isEditingLogoUrl = isEditingBusinessDetails && logoSource === "url";
  const hasInvalidLogoUrl = isEditingLogoUrl && normalizedLogoUrl == null;
  const canSave =
    advertiserNameError == null &&
    titleError == null &&
    bodyError == null &&
    hasImage &&
    !isRegeneratingImage &&
    !isUploadingLogo &&
    !hasInvalidLogoUrl;

  const websiteHostname = (() => {
    try {
      return new URL(normalizePublicWebsiteUrl(websiteUrl)).hostname;
    } catch {
      return websiteUrl;
    }
  })();

  const uploadLogo = async (source: File | string) => {
    const uploadVersion = logoUploadVersionRef.current + 1;
    logoUploadVersionRef.current = uploadVersion;
    setIsUploadingLogo(true);
    setLogoUploadFailed(false);
    try {
      const uploadedLogo = await onUploadLogo(source);
      if (logoUploadVersionRef.current !== uploadVersion) {
        return null;
      }
      setDraftLogoOverride(uploadedLogo);
      setDidLogoImageFail(false);
      setIsUploadingLogo(false);
      return uploadedLogo;
    } catch {
      if (logoUploadVersionRef.current === uploadVersion) {
        setLogoUploadFailed(true);
        setIsUploadingLogo(false);
      }
      return null;
    }
  };

  return (
    <Modal open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <Modal.Content
        backdrop="fade"
        className={`w-[662px] max-w-[calc(100vw-2rem)] rounded-2xl border border-[rgba(13,13,13,0.1)] ${
          isEditingBusinessDetails ? "md:h-[832px]"
          : hasLogo ? "md:h-[738px]"
          : "md:h-[772px]"
        }`}
      >
        <Modal.Form
          className={`h-full px-6 pt-6 pb-8 ${isEditingBusinessDetails ? "gap-4" : "gap-8"}`}
          onSubmit={async (event) => {
            event.preventDefault();
            if (!canSave) {
              return;
            }
            let nextLogoOverride = draftLogoOverride;
            if (
              isEditingLogoUrl &&
              normalizedLogoUrl != null &&
              ((!hasLogo && draftLogoOverride !== null) ||
                normalizedLogoUrl !== normalizePublicWebsiteUrl(websiteUrl))
            ) {
              const uploadedLogo = await uploadLogo(draftLogoUrl.trim());
              if (uploadedLogo == null) {
                return;
              }
              nextLogoOverride = uploadedLogo;
            }
            onSave(
              { body: draft.body.trim(), title: draft.title.trim() },
              draftAdvertiserName.trim(),
              nextLogoOverride,
            );
          }}
        >
          <Modal.Body className={`${isEditingBusinessDetails ? "space-y-4" : "space-y-8"} !p-0`}>
            <DialogHeader
              closeLabel={intl.formatMessage(messages.closeEditAd)}
              description={intl.formatMessage(messages.editAdDescription)}
              title={intl.formatMessage(messages.editAdPreview)}
            />

            <div className="space-y-4">
              <label className="block space-y-1">
                <span className="text-primary block text-sm leading-[22px] font-medium">
                  {intl.formatMessage(messages.advertiserName)}
                </span>
                <Input
                  aria-describedby={advertiserNameError ? advertiserFeedbackId : undefined}
                  aria-invalid={advertiserNameError != null}
                  aria-label={intl.formatMessage(messages.advertiserName)}
                  invalid={advertiserNameError != null}
                  maxLength={ONBOARDING_BUSINESS_NAME_MAX_LENGTH}
                  onChange={(event) => setDraftAdvertiserName(event.currentTarget.value)}
                  size="xl"
                  value={draftAdvertiserName}
                />
                {advertiserNameError ?
                  <span
                    className="text-danger block text-xs leading-4"
                    id={advertiserFeedbackId}
                    role="alert"
                  >
                    {advertiserNameError}
                  </span>
                : null}
              </label>

              <div className="space-y-3">
                <span className="text-primary block text-sm leading-[22px] font-medium">
                  {intl.formatMessage(messages.businessLogo)}
                </span>
                <div className="flex min-h-10 items-center gap-4">
                  {hasLogo ?
                    <img
                      alt=""
                      className="size-10 shrink-0 rounded-full object-contain"
                      onError={() => setDidLogoImageFail(true)}
                      src={displayedLogoUrl ?? undefined}
                    />
                  : <span className="border-warning text-warning relative flex size-10 shrink-0 items-center justify-center rounded-full border">
                      <FileUpload aria-hidden="true" className="size-4" />
                      <Warning
                        aria-hidden="true"
                        className="bg-surface-primary absolute -top-1 -right-1 size-4 rounded-full"
                      />
                    </span>
                  }
                  {!isEditingBusinessDetails ?
                    <div className="min-w-0">
                      <div className="text-primary text-sm font-medium">
                        {intl.formatMessage(messages.logoImageUrl)}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-secondary truncate text-sm">{websiteHostname}</span>
                        <Button
                          aria-label={intl.formatMessage(messages.editBusinessLogo)}
                          color="secondary"
                          disabled={isUploadingLogo}
                          iconSize="sm"
                          onClick={() => setIsEditingBusinessDetails(true)}
                          size="xs"
                          type="button"
                          uniform
                          variant="ghost"
                        >
                          <Pencil aria-hidden="true" className="size-4" />
                        </Button>
                      </div>
                    </div>
                  : <RadioGroup
                      aria-label={intl.formatMessage(messages.businessLogo)}
                      direction="row"
                      onChange={(value) => {
                        setLogoSource(value as "url" | "upload");
                        setLogoUploadFailed(false);
                      }}
                      value={logoSource}
                    >
                      <RadioGroup.Item value="url">
                        {intl.formatMessage(messages.useLogoUrl)}
                      </RadioGroup.Item>
                      <RadioGroup.Item value="upload">
                        {intl.formatMessage(messages.uploadLogoImage)}
                      </RadioGroup.Item>
                    </RadioGroup>
                  }
                </div>

                {!hasLogo && !isEditingBusinessDetails ?
                  <p className="text-warning m-0 text-sm">
                    <button
                      className="text-warning underline underline-offset-2"
                      onClick={() => setIsEditingBusinessDetails(true)}
                      type="button"
                    >
                      {intl.formatMessage(messages.uploadBusinessLogo)}
                    </button>{" "}
                    {intl.formatMessage(messages.reviewSuggestedBusinessDetails)}
                  </p>
                : null}

                {isEditingBusinessDetails ?
                  <div className="space-y-3">
                    {logoSource === "url" ?
                      <label className="block space-y-2">
                        <span className="text-primary block text-sm font-medium">
                          {intl.formatMessage(messages.enterLogoUrl)}
                        </span>
                        <Input
                          aria-describedby={hasInvalidLogoUrl ? logoFeedbackId : undefined}
                          aria-invalid={hasInvalidLogoUrl}
                          aria-label={intl.formatMessage(messages.enterLogoUrl)}
                          className="h-[38px]"
                          invalid={hasInvalidLogoUrl}
                          onChange={(event) => {
                            setDraftLogoUrl(event.currentTarget.value);
                            setLogoUploadFailed(false);
                          }}
                          size="lg"
                          startAdornment={<LinkIcon className="text-secondary ml-3 size-4" />}
                          value={draftLogoUrl}
                        />
                      </label>
                    : <FileSelector
                        accept={{
                          "image/gif": [],
                          "image/jpeg": [],
                          "image/jpg": [],
                          "image/png": [],
                          "image/webp": [],
                        }}
                        asTrigger
                        multiple={false}
                        onFiles={(files) => {
                          const file = files[0];
                          if (file != null) {
                            void uploadLogo(file);
                          }
                        }}
                        onRejections={() => setLogoUploadFailed(true)}
                      >
                        <Button
                          aria-describedby={logoUploadFailed ? logoFeedbackId : undefined}
                          color="secondary"
                          disabled={isUploadingLogo}
                          iconSize="sm"
                          loading={isUploadingLogo}
                          pill
                          size="xs"
                          type="button"
                          variant="outline"
                        >
                          <FileUpload aria-hidden="true" />
                          {intl.formatMessage(
                            displayedLogoUrl ? messages.replaceLogo : messages.uploadLogo,
                          )}
                        </Button>
                      </FileSelector>
                    }

                    {hasLogo ?
                      <Button
                        aria-label={intl.formatMessage(messages.removeLogo)}
                        color="secondary"
                        disabled={isUploadingLogo}
                        onClick={() => {
                          setDraftLogoOverride(null);
                          setDidLogoImageFail(false);
                          setLogoUploadFailed(false);
                        }}
                        size="xs"
                        type="button"
                        variant="ghost"
                      >
                        <X aria-hidden="true" />
                        {intl.formatMessage(messages.removeLogo)}
                      </Button>
                    : null}
                  </div>
                : null}

                {hasInvalidLogoUrl || logoUploadFailed ?
                  <span
                    className="text-danger block text-xs leading-4"
                    id={logoFeedbackId}
                    role="alert"
                  >
                    {intl.formatMessage(
                      hasInvalidLogoUrl ? messages.invalidLogoUrl : messages.logoUploadFailed,
                    )}
                  </span>
                : null}
              </div>

              <label className="block space-y-1">
                <span className="text-primary block text-sm leading-[22px] font-medium">
                  {intl.formatMessage(messages.adTitle)}
                </span>
                <Input
                  aria-describedby={titleError ? titleFeedbackId : undefined}
                  aria-invalid={titleError != null}
                  aria-label={intl.formatMessage(messages.adTitle)}
                  endAdornment={
                    <CharacterCount
                      length={draft.title.length}
                      maxLength={CARPET_LITE_AD_TITLE_MAX_LENGTH}
                    />
                  }
                  invalid={titleError != null}
                  maxLength={CARPET_LITE_AD_TITLE_MAX_LENGTH}
                  onChange={(event) => {
                    const title = event.currentTarget.value;
                    setDraft((current) => ({ ...current, title }));
                  }}
                  size="xl"
                  value={draft.title}
                />
                {titleError ?
                  <span
                    className="text-danger block text-xs leading-4"
                    id={titleFeedbackId}
                    role="alert"
                  >
                    {titleError}
                  </span>
                : null}
              </label>

              <label className="block space-y-1">
                <span className="text-primary block text-sm leading-[22px] font-medium">
                  {intl.formatMessage(messages.adDescription)}
                </span>
                <Input
                  aria-describedby={bodyError ? bodyFeedbackId : undefined}
                  aria-invalid={bodyError != null}
                  aria-label={intl.formatMessage(messages.adDescription)}
                  endAdornment={
                    <CharacterCount
                      length={draft.body.length}
                      maxLength={CARPET_LITE_AD_BODY_MAX_LENGTH}
                    />
                  }
                  invalid={bodyError != null}
                  maxLength={CARPET_LITE_AD_BODY_MAX_LENGTH}
                  onChange={(event) => {
                    const body = event.currentTarget.value;
                    setDraft((current) => ({ ...current, body }));
                  }}
                  size="xl"
                  value={draft.body}
                />
                {bodyError ?
                  <span
                    className="text-danger block text-xs leading-4"
                    id={bodyFeedbackId}
                    role="alert"
                  >
                    {bodyError}
                  </span>
                : null}
              </label>

              <div className="space-y-4">
                <div className="space-y-1">
                  <span className="text-primary block text-sm leading-[22px] font-medium">
                    {intl.formatMessage(messages.adImages)}
                  </span>
                  <p className="text-secondary m-0 text-xs leading-4">
                    {intl.formatMessage(messages.adImagesHelp)}
                  </p>
                </div>
                <div className="space-y-1">
                  {hasImage ?
                    <div className="flex items-start gap-1.5">
                      <img
                        alt=""
                        className="size-[100px] object-cover"
                        src={generatedImageUrl ?? undefined}
                      />
                      <Button
                        aria-label={intl.formatMessage(messages.removeAdImage)}
                        className="-mt-3 -ml-3"
                        color="secondary"
                        iconSize="sm"
                        onClick={() => setIsImageRemoved(true)}
                        size="2xl"
                        type="button"
                        uniform
                        variant="ghost"
                      >
                        <X aria-hidden="true" />
                      </Button>
                    </div>
                  : null}
                  <Button
                    aria-describedby={regenerationFailed ? imageStatusId : undefined}
                    color="secondary"
                    disabled={isRegeneratingImage}
                    iconSize="sm"
                    loading={isRegeneratingImage}
                    onClick={async () => {
                      setRegenerationFailed(false);
                      try {
                        if (await onRegenerateImage()) {
                          setIsImageRemoved(false);
                        } else {
                          setRegenerationFailed(true);
                        }
                      } catch {
                        setRegenerationFailed(true);
                      }
                    }}
                    pill
                    size="xs"
                    type="button"
                    variant="outline"
                  >
                    <Reload aria-hidden="true" />
                    {intl.formatMessage(messages.regenerateAdImage)}
                  </Button>
                  {regenerationFailed ?
                    <span
                      className="text-danger block text-xs leading-4"
                      id={imageStatusId}
                      role="alert"
                    >
                      {intl.formatMessage(messages.adImageRegenerationFailed)}
                    </span>
                  : !hasImage ?
                    <span className="sr-only" id={imageStatusId} role="status">
                      {intl.formatMessage(messages.adImageRequired)}
                    </span>
                  : null}
                </div>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer className="gap-6 !p-0">
            <Modal.Close>
              <Button
                className="flex-1"
                color="primary"
                pill
                size="2xl"
                type="button"
                variant="outline"
              >
                {intl.formatMessage(messages.discard)}
              </Button>
            </Modal.Close>
            <Button
              aria-describedby={!hasImage || regenerationFailed ? imageStatusId : undefined}
              className="flex-1"
              color="primary"
              disabled={!canSave}
              pill
              size="2xl"
              type="submit"
            >
              {intl.formatMessage(messages.save)}
            </Button>
          </Modal.Footer>
        </Modal.Form>
      </Modal.Content>
    </Modal>
  );
}

export function CarpetLiteEditCampaignDialog({
  countryOptions,
  destinationUrl,
  destinationUrlSuffix,
  open,
  settings,
  onClose,
  onSave,
}: {
  countryOptions: CountryOption[];
  destinationUrl: string;
  destinationUrlSuffix: string;
  open: boolean;
  settings: CarpetLiteCampaignSettings;
  onClose: () => void;
  onSave: (settings: CarpetLiteCampaignSettings, destinationUrlSuffix: string) => void;
}) {
  const intl = useIntl();
  const { minDailyCampaignBudgetMicros, minLifetimeCampaignBudgetMicros } =
    useDailyCampaignBudgetConfig({ currency: DEFAULT_ACCOUNT_CURRENCY });
  const maxBidCapMicrosByCurrency = useAdGroupMaxBidCapMicrosByCurrency();
  const [draft, setDraft] = useState(settings);
  const [draftDestinationUrlSuffix, setDraftDestinationUrlSuffix] = useState(destinationUrlSuffix);
  const wasOpenRef = useRef(false);
  const budgetHelpId = useId();
  const budgetErrorId = useId();
  const bidErrorId = useId();
  const destinationHelpId = useId();
  const destinationErrorId = useId();

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDraft(settings);
      setDraftDestinationUrlSuffix(destinationUrlSuffix);
    }
    wasOpenRef.current = open;
  }, [destinationUrlSuffix, open, settings]);

  const selectedCountryCode = draft.locationCountryCodes[0] ?? countryOptions[0]?.value ?? "US";
  const budgetError = useMemo(() => {
    if (!/^\d+(?:\.\d{1,2})?$/.test(draft.budgetAmount) || Number(draft.budgetAmount) <= 0) {
      return intl.formatMessage(messages.invalidBudget);
    }
    if (
      Number(draft.budgetAmount) <
      getCampaignBudgetMinimumAmount(
        draft.budgetType,
        minDailyCampaignBudgetMicros,
        minLifetimeCampaignBudgetMicros,
      )
    ) {
      return getCampaignBudgetMinimumErrorMessage(
        draft.budgetType,
        intl,
        minDailyCampaignBudgetMicros,
        minLifetimeCampaignBudgetMicros,
      );
    }
    return null;
  }, [
    draft.budgetAmount,
    draft.budgetType,
    intl,
    minDailyCampaignBudgetMicros,
    minLifetimeCampaignBudgetMicros,
  ]);
  const maxBidAmount =
    draft.maxBidAmount ?? getDefaultClickMaxBidAmountForCurrency(DEFAULT_ACCOUNT_CURRENCY);
  const bidError = useMemo(() => {
    if (draft.bidMode !== "manual") {
      return null;
    }
    if (!/^\d+(?:\.\d{1,2})?$/.test(maxBidAmount)) {
      return intl.formatMessage(messages.invalidManualBid);
    }
    return draft.objective === "clicks" ?
        getClickObjectiveMaxBidValidationError(
          maxBidAmount,
          DEFAULT_ACCOUNT_CURRENCY,
          intl,
          "clicks",
          maxBidCapMicrosByCurrency,
        )
      : getImpressionObjectiveMaxBidValidationError(
          maxBidAmount,
          intl,
          DEFAULT_ACCOUNT_CURRENCY,
          maxBidCapMicrosByCurrency,
        );
  }, [draft.bidMode, draft.objective, intl, maxBidAmount, maxBidCapMicrosByCurrency]);
  const destinationUrlWithSuffix = useMemo(
    () => buildCarpetLiteDestinationUrl(destinationUrl, draftDestinationUrlSuffix),
    [destinationUrl, draftDestinationUrlSuffix],
  );
  const immutableDestinationUrl = useMemo(
    () =>
      getDestinationUrlInputPrefix(
        buildCarpetLiteDestinationUrl(destinationUrl, "") ?? destinationUrl,
      ),
    [destinationUrl],
  );
  const isDestinationUrlAppendable = useMemo(() => {
    try {
      const url = new URL(destinationUrl);
      return url.search.length === 0 && url.hash.length === 0;
    } catch {
      return false;
    }
  }, [destinationUrl]);
  const destinationUrlInputValue = `${immutableDestinationUrl}${draftDestinationUrlSuffix}`;
  const destinationUrlSuffixError =
    destinationUrlWithSuffix == null ?
      intl.formatMessage(messages.invalidDestinationUrlSuffix)
    : null;
  const canSave = budgetError == null && bidError == null && destinationUrlSuffixError == null;

  return (
    <Modal open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <Modal.Content
        backdrop="fade"
        className="w-[662px] max-w-[calc(100vw-2rem)] rounded-2xl border border-[rgba(13,13,13,0.1)] md:h-auto md:max-h-[calc(100vh-2rem)]"
      >
        <Modal.Form
          className="h-full gap-8 px-6 pt-6 pb-8"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSave) {
              if (draft.bidMode === "manual") {
                onSave({ ...draft, maxBidAmount }, draftDestinationUrlSuffix);
              } else {
                const { maxBidAmount: _maxBidAmount, ...automatedSettings } = draft;
                onSave(automatedSettings, draftDestinationUrlSuffix);
              }
            }
          }}
        >
          <Modal.Body className="space-y-8 !p-0">
            <DialogHeader
              closeLabel={intl.formatMessage(messages.closeEditCampaignDetails)}
              description={intl.formatMessage(messages.editCampaignDetailsDescription)}
              title={intl.formatMessage(messages.editCampaignDetails)}
            />

            <div className="space-y-[14px]">
              <label className="block space-y-2">
                <span className="text-primary block text-sm leading-[22px] font-medium">
                  {intl.formatMessage(messages.objective)}
                </span>
                <Select
                  aria-label={intl.formatMessage(messages.objective)}
                  dropdownIconType="chevronDown"
                  onChange={(option) => {
                    const objective = option.value as CarpetLiteCampaignSettings["objective"];
                    setDraft((current) => ({
                      ...current,
                      objective,
                      ...(objective === "reach" ? { bidMode: "manual" as const } : {}),
                    }));
                  }}
                  options={[
                    { label: intl.formatMessage(messages.clicks), value: "clicks" },
                    { label: intl.formatMessage(messages.reach), value: "reach" },
                  ]}
                  size="xl"
                  triggerClassName="w-full"
                  value={draft.objective}
                />
                <span className="text-secondary block text-xs leading-4">
                  {intl.formatMessage(messages.objectiveHelp)}
                </span>
              </label>

              <label className="block space-y-1">
                <span className="text-primary block text-sm leading-[22px] font-medium">
                  {intl.formatMessage(messages.campaignLocations)}
                </span>
                <Select
                  aria-label={intl.formatMessage(messages.locations)}
                  dropdownIconType="chevronDown"
                  onChange={(option) =>
                    setDraft((current) => ({
                      ...current,
                      locationCountryCodes: [option.value],
                    }))
                  }
                  options={countryOptions}
                  searchable
                  size="xl"
                  triggerClassName="w-full"
                  value={selectedCountryCode}
                />
              </label>

              <div className="space-y-2">
                <span className="text-primary block text-sm leading-5 font-medium">
                  {intl.formatMessage(messages.budget)}
                </span>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Select
                    aria-label={intl.formatMessage(messages.budgetType)}
                    dropdownIconType="chevronDown"
                    onChange={(option) => {
                      const budgetType = option.value as CarpetLiteCampaignSettings["budgetType"];
                      setDraft((current) => ({
                        ...current,
                        budgetType,
                        ...(budgetType === "lifetime" ? { bidMode: "manual" as const } : {}),
                      }));
                    }}
                    options={[
                      { label: intl.formatMessage(messages.dailyBudget), value: "daily" },
                      { label: intl.formatMessage(messages.campaignTotal), value: "lifetime" },
                    ]}
                    size="xl"
                    triggerClassName="w-full"
                    value={draft.budgetType}
                  />
                  <Input
                    aria-describedby={`${budgetHelpId}${budgetError ? ` ${budgetErrorId}` : ""}`}
                    aria-invalid={budgetError != null}
                    aria-label={intl.formatMessage(messages.budgetAmount)}
                    endAdornment={
                      <span className="text-secondary pr-4 text-sm">
                        {DEFAULT_ACCOUNT_CURRENCY}
                      </span>
                    }
                    inputMode="decimal"
                    invalid={budgetError != null}
                    maxLength={10}
                    onChange={(event) => {
                      const budgetAmount = event.currentTarget.value;
                      setDraft((current) => ({ ...current, budgetAmount }));
                    }}
                    size="xl"
                    startAdornment={<span className="text-secondary pl-3 text-sm">$</span>}
                    value={draft.budgetAmount}
                  />
                </div>
                <span className="text-secondary block text-xs leading-4" id={budgetHelpId}>
                  {intl.formatMessage(messages.budgetHelp)}
                </span>
                {budgetError ?
                  <span
                    className="text-danger block text-xs leading-4"
                    id={budgetErrorId}
                    role="alert"
                  >
                    {budgetError}
                  </span>
                : null}
              </div>

              <div className="space-y-1">
                <span className="text-primary block text-sm leading-[22px] font-medium">
                  {intl.formatMessage(messages.bidStrategy)}
                </span>
                <div className="grid grid-cols-1 items-center gap-3 sm:grid-cols-2">
                  <Select
                    aria-label={intl.formatMessage(messages.bidStrategy)}
                    dropdownIconType="chevronDown"
                    onChange={(option) =>
                      setDraft((current) => ({
                        ...current,
                        bidMode: option.value as CarpetLiteCampaignSettings["bidMode"],
                      }))
                    }
                    options={[
                      ...(draft.objective === "clicks" && draft.budgetType === "daily" ?
                        [
                          {
                            label: intl.formatMessage(messages.maximumResults),
                            value: "automated",
                          },
                        ]
                      : []),
                      { label: intl.formatMessage(messages.manualBid), value: "manual" },
                    ]}
                    size="xl"
                    triggerClassName="w-full"
                    value={draft.bidMode}
                  />
                  {draft.bidMode === "manual" ?
                    <Input
                      aria-describedby={bidError ? bidErrorId : undefined}
                      aria-invalid={bidError != null}
                      aria-label={intl.formatMessage(messages.manualBidAmount)}
                      endAdornment={
                        <span className="text-secondary pr-4 text-sm">
                          {DEFAULT_ACCOUNT_CURRENCY}
                        </span>
                      }
                      inputMode="decimal"
                      invalid={bidError != null}
                      maxLength={10}
                      onChange={(event) => {
                        const nextMaxBidAmount = event.currentTarget.value;
                        setDraft((current) => ({ ...current, maxBidAmount: nextMaxBidAmount }));
                      }}
                      size="xl"
                      startAdornment={<span className="text-secondary pl-3 text-sm">$</span>}
                      value={maxBidAmount}
                    />
                  : <span className="text-secondary text-xs leading-4">
                      {intl.formatMessage(messages.maximumResultsHelp)}
                    </span>
                  }
                </div>
                {bidError ?
                  <span
                    className="text-danger block text-xs leading-4"
                    id={bidErrorId}
                    role="alert"
                  >
                    {bidError}
                  </span>
                : null}
              </div>

              <label className="block space-y-2">
                <span className="text-primary block text-sm leading-[22px] font-medium">
                  {intl.formatMessage(messages.destinationUrl)}
                </span>
                <Input
                  aria-describedby={`${destinationHelpId}${destinationUrlSuffixError ? ` ${destinationErrorId}` : ""}`}
                  aria-invalid={destinationUrlSuffixError != null}
                  aria-label={intl.formatMessage(messages.destinationUrl)}
                  invalid={destinationUrlSuffixError != null}
                  maxLength={CARPET_LITE_TARGET_URL_MAX_LENGTH}
                  readOnly={!isDestinationUrlAppendable}
                  onClick={(event) => {
                    clampDestinationUrlSelection(
                      event.currentTarget,
                      immutableDestinationUrl.length,
                    );
                  }}
                  onChange={(event) => {
                    if (!isDestinationUrlAppendable) {
                      return;
                    }
                    const nextValue = event.currentTarget.value;
                    if (!nextValue.startsWith(immutableDestinationUrl)) {
                      event.currentTarget.value = destinationUrlInputValue;
                      clampDestinationUrlSelection(
                        event.currentTarget,
                        immutableDestinationUrl.length,
                      );
                      return;
                    }
                    const suffix = nextValue.slice(immutableDestinationUrl.length);
                    setDraftDestinationUrlSuffix(suffix);
                  }}
                  onFocus={(event) => {
                    clampDestinationUrlSelection(
                      event.currentTarget,
                      immutableDestinationUrl.length,
                    );
                  }}
                  onKeyDown={(event) => {
                    if (!isDestinationUrlAppendable) {
                      return;
                    }
                    const input = event.currentTarget;
                    const selectionStart = input.selectionStart ?? immutableDestinationUrl.length;
                    const selectionEnd = input.selectionEnd ?? immutableDestinationUrl.length;
                    const isSelectAll =
                      (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "a";
                    if (isSelectAll) {
                      event.preventDefault();
                      input.setSelectionRange(immutableDestinationUrl.length, input.value.length);
                      return;
                    }
                    if (event.key === "Home") {
                      event.preventDefault();
                      input.setSelectionRange(
                        immutableDestinationUrl.length,
                        event.shiftKey ? selectionEnd : immutableDestinationUrl.length,
                      );
                      return;
                    }
                    const backspaceWouldEditPrefix =
                      event.key === "Backspace" &&
                      (selectionStart < immutableDestinationUrl.length ||
                        (selectionStart === immutableDestinationUrl.length &&
                          selectionEnd === immutableDestinationUrl.length));
                    const deleteWouldEditPrefix =
                      event.key === "Delete" && selectionStart < immutableDestinationUrl.length;
                    if (backspaceWouldEditPrefix || deleteWouldEditPrefix) {
                      event.preventDefault();
                      clampDestinationUrlSelection(input, immutableDestinationUrl.length);
                    }
                  }}
                  onSelect={(event) => {
                    clampDestinationUrlSelection(
                      event.currentTarget,
                      immutableDestinationUrl.length,
                    );
                  }}
                  size="xl"
                  value={destinationUrlInputValue}
                />
                <span className="text-secondary block text-xs leading-4" id={destinationHelpId}>
                  {intl.formatMessage(
                    isDestinationUrlAppendable ?
                      messages.destinationUrlSuffixHelp
                    : messages.destinationUrlLockedHelp,
                  )}
                </span>
                {destinationUrlSuffixError ?
                  <span
                    className="text-danger block text-xs leading-4"
                    id={destinationErrorId}
                    role="alert"
                  >
                    {destinationUrlSuffixError}
                  </span>
                : null}
              </label>
            </div>
          </Modal.Body>

          <Modal.Footer className="gap-6 !p-0">
            <Modal.Close>
              <Button
                className="flex-1"
                color="primary"
                pill
                size="2xl"
                type="button"
                variant="outline"
              >
                {intl.formatMessage(messages.discard)}
              </Button>
            </Modal.Close>
            <Button
              className="flex-1"
              color="primary"
              disabled={!canSave}
              pill
              size="2xl"
              type="submit"
            >
              {intl.formatMessage(messages.save)}
            </Button>
          </Modal.Footer>
        </Modal.Form>
      </Modal.Content>
    </Modal>
  );
}
